import React, { useEffect, useState } from 'react';
import { useAors } from 'hooks/useAors';
import { createRoot } from 'react-dom/client';

import LayerDataPopup from '../mapComponents/LayerDataPopup.js';
import GeolocationLayerPopup from '../mapComponents/GeolocationLayerPopup.js';
import ModelLayerPopup from '../mapComponents/ModelLayerPopup.js';
import EventLayerPopup from '../mapComponents/EventLayerPopup.js';
import { eriScenarios } from '../constants/eri.js';
import TaskManagementMapLayerPopup from 'components/TaskManagement/TaskManagementMapLayerPopup.js';
import CommonLayerPopup from '../mapComponents/CommonLayerPopup.js';
import Popup from '../mapComponents/Popup.js';
import PowerOutageLayerPopup from '../mapComponents/PowerOutageLayerPopup.js';
import TransportationMapLayerPopup from '../mapComponents/TransportationMapLayerPopup.js';

import {
  hurricane_cones_source_id,
  hurricane_paths_source_id,
  hurricane_points_source_id,
} from '../mapEffects/mapEventsEffects/configureHurricaneLayers';

import {
  silurian_cones_source_id,
  silurian_points_source_id,
  silurian_paths_source_id,
} from '../mapEffects/mapEventsEffects/configureSilurianProjectionLayers';

import { earthquakes_source_layer } from '../mapEffects/mapEventsEffects/configureEarthquakeLayers';

import {
  polygon_poweroutage_source_id,
  polygon_poweroutage_source_layer,
} from '../mapEffects/mapEventsEffects/configurePowerOutageLayers';

import { polygon_events_source_id } from '../mapEffects/mapEventsEffects/configureNwsAndWildfiresLayers';

import {
  custom_point_events_source_id,
  custom_polygon_events_source_id,
} from '../mapEffects/mapEventsEffects/configureCustomEventsLayers.js';

const eventSourceIDs = [
  polygon_events_source_id,
  custom_polygon_events_source_id,
  custom_point_events_source_id,
  hurricane_cones_source_id,
  hurricane_paths_source_id,
  hurricane_points_source_id,
  polygon_poweroutage_source_id,
  polygon_poweroutage_source_layer,
  earthquakes_source_layer,
  silurian_cones_source_id,
  silurian_paths_source_id,
  silurian_points_source_id,
];

function filterEarthquakeDuplicates(arr) {
  const seenIds = new Set();

  return arr.filter((item) => {
    if (item.source === 'earthquakes_source_layer') {
      const id = item?.properties?.id;
      if (id && !seenIds.has(id)) {
        seenIds.add(id);
        return true;
      }
      return false;
    }
    return true;
  });
}

const useMapLayout = (params) => {
  const {
    mapboxgl,
    map,
    mapSettings,
    owmToken,
    layers,
    clickedLayers,
    sources,
    setLayers,
    updateLayer,
    setSelectedGeolocation,
    setBottomPanelActive,
    eventSelected,
    taskSelected,
    nodeSelected,
    sidebarSecondaryHandler,
    setSidebarActiveItem,
    setSelectedEventTabNews,
    setClickedLayers,
    setSources,
    handleCustomEventSelected,
    eventLayerState,
    setSidebarSecondaryActiveItem,
  } = params;

  const popupPlaceholder = document.createElement('div');
  const popupRoot = createRoot(popupPlaceholder);
  const eriScenario = mapSettings?.eriScenario;

  const [selectedModelFeature, setSelectedModelFeature] = useState();
  const [iconWasAddedToMap, setIconWasAddedToMap] = useState(false);

  const { data: aors = [] } = useAors();

  useEffect(() => {
    if (clickedLayers?.length > 0) {
      const popup = new mapboxgl.Popup({ closeOnClick: false });
      const popups = document.getElementsByClassName('mapboxgl-popup');
      if (popups.length) {
        popups[0].remove();
      }
      const firstLayerData = clickedLayers[0].data;

      popupRoot.render(
        <CommonLayerPopup>
          {clickedLayers.map((clickedLayer, index) => {
            const layerClicked = clickedLayer.data;
            const layer = layers.find((l) => l.id === layerClicked.layer.id);

            if (layerClicked.layer.metadata.usermade) {
              return (
                <LayerDataPopup
                  key={'data-popup-' + index}
                  layer={layer}
                  layers={layers}
                  setLayers={setLayers}
                  updateLayer={updateLayer}
                  clickPopup={popup}
                  e={layerClicked.e}
                  feature={layerClicked.features[0]}
                />
              );
            } else if (layerClicked.layer.metadata.geolocation) {
              setSelectedModelFeature(layerClicked.features[0]);
              return (
                <GeolocationLayerPopup
                  key={'geolocation-popup-' + index}
                  clickPopup={popup}
                  features={layerClicked.features}
                  AORs={aors}
                  setSelectedGeolocation={(geolocation, type) => {
                    setSelectedGeolocation({ geolocation, type });
                    setSidebarActiveItem('Geolocations');
                  }}
                  setSidebarSecondaryActiveItem={setSidebarSecondaryActiveItem}
                />
              );
            } else if (layerClicked.layer.metadata.taskLayer) {
              setSelectedModelFeature(layerClicked.features);
              return (
                <TaskManagementMapLayerPopup
                  key={'task-management-popup-' + index}
                  features={layerClicked.features}
                />
              );
            } else if (layerClicked.layer.metadata.model_layer) {
              setSelectedModelFeature(layerClicked.features[0]);
              setBottomPanelActive({
                layer: layerClicked.layer,
                feature: layerClicked.features[0],
                isERI: true,
              });
              return (
                <ModelLayerPopup
                  key={'model-layer-popup-' + index}
                  layer={layerClicked.layer}
                  clickPopup={popup}
                  e={layerClicked.e}
                  feature={layerClicked.features[0]}
                  setBottomPanelActive={setBottomPanelActive}
                  eriScenario={eriScenario}
                  eriScenarios={eriScenarios}
                  selectedDatetime={mapSettings?.selectedDatetime}
                />
              );
            } else if (layerClicked.layer.metadata.poc_layer) {
              setSelectedModelFeature(layerClicked.features[0]);
              setBottomPanelActive({
                layer: layerClicked.layer,
                feature: layerClicked.features[0],
                isPowerOutage: true,
              });
              return (
                <PowerOutageLayerPopup
                  key={'model-power-outage-popup-' + index}
                  layer={layerClicked.layer}
                  clickPopup={popup}
                  e={layerClicked.e}
                  feature={layerClicked.features[0]}
                  selectedDatetime={mapSettings?.selectedDatetime}
                />
              );
            } else if (layerClicked.layer.metadata.transportationLayer) {
              setSelectedModelFeature(layerClicked.features);
              return (
                <TransportationMapLayerPopup
                  key={'model-transportation-popup-' + index}
                  features={layerClicked.features}
                />
              );
            } else if (layerClicked.layer.metadata.eventlayer) {
              const mapCurrent = map.current;
              const point = layerClicked.e.point;
              const features = filterEarthquakeDuplicates(
                mapCurrent
                  .queryRenderedFeatures(point)
                  .filter((f) => eventSourceIDs.includes(f.source))
              );

              return (
                <EventLayerPopup
                  key={'event-layer-popup-' + index}
                  features={features}
                  events={eventLayerState.ethelData}
                  handleEventSelected={(event) => {
                    eventSelected(event);
                    sidebarSecondaryHandler(true, 'Selected Event');
                    setSidebarActiveItem('Events');
                    setSelectedEventTabNews();
                  }}
                  handleCustomEventSelected={handleCustomEventSelected}
                />
              );
            } else if (layerClicked.layer.metadata.owmLayer) {
              setBottomPanelActive({
                layer: mapSettings?.weatherLayer,
                weatherData: layerClicked.e,
                isWeather: true,
              });
              return mapSettings?.weatherLayer && layerClicked.e ? (
                <Popup
                  key={'data-popup-owm' + index}
                  weatherLayer={mapSettings?.weatherLayer}
                  weatherData={layerClicked.e}
                  owmToken={owmToken}
                  selectedDatetime={mapSettings?.selectedDatetime}
                />
              ) : null;
            }
          })}
        </CommonLayerPopup>
      );
      popup
        .setLngLat(firstLayerData.e.lngLat)
        .setDOMContent(popupPlaceholder)
        .addTo(map.current);
      setClickedLayers([]);
    }
  }, [
    clickedLayers,
    eriScenario,
    eventLayerState.ethelData,
    eventSelected,
    handleCustomEventSelected,
    iconWasAddedToMap,
    layers,
    map,
    mapSettings?.selectedDatetime,
    mapSettings?.weatherLayer,
    nodeSelected,
    owmToken,
    aors,
    setBottomPanelActive,
    setClickedLayers,
    setIconWasAddedToMap,
    setLayers,
    setSelectedEventTabNews,
    setSelectedGeolocation,
    setSelectedModelFeature,
    setSidebarActiveItem,
    setSidebarSecondaryActiveItem,
    setSources,
    sidebarSecondaryHandler,
    sources,
    taskSelected,
    updateLayer,
    mapboxgl.Popup,
    popupPlaceholder,
    popupRoot,
  ]);

  useEffect(() => {
    if (iconWasAddedToMap) {
      const usermadeLayers = map.current
        .getStyle()
        .layers.filter(
          (l) =>
            !!l.metadata &&
            !!l.metadata.usermade &&
            !layers.find((ll) => ll.id === l.id)
        );
      let newSources = [];
      let newLayers = [];
      usermadeLayers.forEach((l) => {
        const source = map.current.getSource(l.source);
        newSources = [...newSources, source];
        newLayers = [...newLayers, l];
      });
      setSources([...sources, ...newSources]);
      setLayers([...layers, ...newLayers]);
      setIconWasAddedToMap(false);
    }
  }, [iconWasAddedToMap, map, layers, sources, setLayers, setSources]);

  return {
    selectedModelFeature,
  };
};

export default useMapLayout;
